import { Controller } from "@hotwired/stimulus";
import "./component.scss";

const DEBOUNCE_DELAY = 300;

export default class extends Controller {
  static targets = ["searchInput", "clearButton", "resultsPanel"];

  connect() {
    this.searchTimeout = null;
    this.abortController = null;
    this.syncClearButton();
    this.focusInputAtEnd();
  }

  disconnect() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  handleInput() {
    this.syncClearButton();

    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    this.searchTimeout = setTimeout(() => {
      this.performSearch();
      this.searchTimeout = null;
    }, DEBOUNCE_DELAY);
  }

  handleSubmit(event) {
    event.preventDefault();

    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }

    this.performSearch();
  }

  clear(event) {
    event.preventDefault();

    if (!this.hasSearchInputTarget) return;

    this.searchInputTarget.value = "";
    this.syncClearButton();
    this.searchInputTarget.focus();

    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }

    this.performSearch();
  }

  async performSearch() {
    if (!this.hasSearchInputTarget || !this.hasResultsPanelTarget) return;

    const form = this.searchInputTarget.form;
    if (!form) return;

    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    const url = new URL(form.action, window.location.origin);
    url.searchParams.set("query", this.searchInputTarget.value);

    try {
      const response = await fetch(url, {
        headers: {
          Accept: "text/html",
          "Turbo-Frame": "dialog_modal",
        },
        signal: this.abortController.signal,
      });

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const newResults = doc.querySelector("[aria-live='polite']");

      if (newResults && this.hasResultsPanelTarget) {
        this.resultsPanelTarget.innerHTML = newResults.innerHTML;
      }
    } catch (e) {
      if (e.name !== "AbortError") throw e;
    }
  }

  focusInputAtEnd() {
    if (!this.hasSearchInputTarget) return;

    requestAnimationFrame(() => {
      const input = this.searchInputTarget;
      const caretPosition = input.value.length;

      input.focus();
      input.setSelectionRange(caretPosition, caretPosition);
    });
  }

  syncClearButton() {
    if (!this.hasClearButtonTarget || !this.hasSearchInputTarget) return;

    this.clearButtonTarget.hidden =
      this.searchInputTarget.value.trim().length === 0;
  }
}
